import React, { useState, useEffect, useRef } from 'react';
import { Formik, Form } from 'formik';
import { Grid, Header, Button, Message } from 'semantic-ui-react';
import { useBookWorksheet } from '../../hooks/useBookWorksheet';
import { characterValidationSchema } from '../../utils/validationSchemas';
import { FormikInput, FormikTextArea, FormikSelect, FormikField, FormChangeDetector } from '../common';

/**
 * CharacterForm component
 * Form for creating and editing characters
 * @param {Object} props - Component props
 * @param {Function} props.onSave - Callback when the form is saved
 * @returns {JSX.Element} CharacterForm component
 */
const CharacterForm = ({ onSave }) => {
  const {
    selectedCharacter,
    characterId,
    bookId,
    createCharacter,
    updateCharacter,
    charactersLoading
  } = useBookWorksheet();

  const [formLoading, setFormLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [error, setError] = useState(null);
  const [formChanged, setFormChanged] = useState(false);
  const formikRef = useRef();

  // Clear success message after 3 seconds
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => {
        setSuccessMessage('');
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  // Form submission handler
  const handleSubmit = async (values) => {
    setFormLoading(true);
    setError(null);
    setSuccessMessage('');

    try {
      let result;

      if (characterId) {
        // Update existing character
        result = await updateCharacter({
          characterId,
          data: values
        });
        setSuccessMessage('Character updated successfully!');
      } else if (bookId) {
        // Create new character
        result = await createCharacter({
          bookId,
          data: values
        });
        setSuccessMessage('Character created successfully!');
      } else {
        throw new Error('No book selected');
      }

      setFormChanged(false);

      // Call the onSave callback if provided
      if (onSave) {
        onSave(result);
      }
    } catch (err) {
      setError(err.message || 'An error occurred while saving the character');
    } finally {
      setFormLoading(false);
    }
  };

  // Reset form to initial values
  const handleReset = () => {
    if (formikRef.current) {
      formikRef.current.resetForm();
      setFormChanged(false);
    }
  };

  // Get initial values for the form
  const getInitialValues = () => {
    return {
      name: selectedCharacter?.name || '',
      role: selectedCharacter?.role || '',
      description: selectedCharacter?.description || '',
      age: selectedCharacter?.age || '',
      gender: selectedCharacter?.gender || '',
      status: selectedCharacter?.status || 'active'
    };
  };

  // Show message if no book is selected
  if (!bookId) {
    return (
      <Message info>
        <Message.Header>No Book Selected</Message.Header>
        <p>Please select a book first to create or edit a character.</p>
      </Message>
    );
  }

  return (
    <div className="bw-form-container">
      <Header as="h3" dividing>
        {characterId ? 'Edit Character' : 'Create New Character'}
      </Header>

      <Formik
        innerRef={formikRef}
        initialValues={getInitialValues()}
        validationSchema={characterValidationSchema}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({ isSubmitting, values, isValid, dirty }) => (
          <Form>
            <FormChangeDetector
              onChange={setFormChanged}
              initialValues={getInitialValues()}
            />

            <Grid>
              <Grid.Row>
                <Grid.Column width={10}>
                  <FormikField
                    name="name"
                    component={FormikInput}
                    label="Character Name"
                    placeholder="Enter character name"
                    fluid
                  />
                </Grid.Column>
                <Grid.Column width={6}>
                  <FormikField
                    name="role"
                    component={FormikInput}
                    label="Role"
                    placeholder="Enter character role"
                    fluid
                  />
                </Grid.Column>
              </Grid.Row>

              <Grid.Row>
                <Grid.Column width={4}>
                  <FormikField
                    name="age"
                    component={FormikInput}
                    label="Age"
                    placeholder="Enter age"
                    fluid
                  />
                </Grid.Column>
                <Grid.Column width={6}>
                  <FormikField
                    name="gender"
                    component={FormikSelect}
                    label="Gender"
                    options={[
                      { key: 'male', text: 'Male', value: 'male' },
                      { key: 'female', text: 'Female', value: 'female' },
                      { key: 'non-binary', text: 'Non-Binary', value: 'non-binary' },
                      { key: 'other', text: 'Other', value: 'other' }
                    ]}
                    placeholder="Select gender"
                    fluid
                  />
                </Grid.Column>
                <Grid.Column width={6}>
                  <FormikField
                    name="status"
                    component={FormikSelect}
                    label="Status"
                    options={[
                      { key: 'active', text: 'Active', value: 'active' },
                      { key: 'draft', text: 'Draft', value: 'draft' },
                      { key: 'archived', text: 'Archived', value: 'archived' }
                    ]}
                    placeholder="Select status"
                    fluid
                  />
                </Grid.Column>
              </Grid.Row>

              <Grid.Row>
                <Grid.Column width={16}>
                  <FormikField
                    name="description"
                    component={FormikTextArea}
                    label="Description"
                    placeholder="Enter a description for this character"
                    rows={4}
                  />
                </Grid.Column>
              </Grid.Row>

              {/* Status messages */}
              {(error || successMessage) && (
                <Grid.Row>
                  <Grid.Column width={16}>
                    {error && (
                      <Message negative>
                        <Message.Header>Error</Message.Header>
                        <p>{error}</p>
                      </Message>
                    )}

                    {successMessage && (
                      <Message positive>
                        <Message.Header>Success</Message.Header>
                        <p>{successMessage}</p>
                      </Message>
                    )}
                  </Grid.Column>
                </Grid.Row>
              )}

              {/* Form buttons */}
              <Grid.Row>
                <Grid.Column width={16}>
                  <Button
                    primary
                    type="submit"
                    loading={formLoading || charactersLoading === 'loading'}
                    disabled={!formChanged || !isValid}
                  >
                    {characterId ? 'Update Character' : 'Create Character'}
                  </Button>

                  {formChanged && (
                    <Button
                      type="button"
                      onClick={handleReset}
                    >
                      Cancel Changes
                    </Button>
                  )}
                </Grid.Column>
              </Grid.Row>
            </Grid>
          </Form>
        )}
      </Formik>
    </div>
  );
};

export default CharacterForm;
